import React, { useEffect, useState, useCallback, useRef, useMemo, useContext, createContext } from 'react';
import { createPortal } from 'react-dom';
import { useParams, Link, Navigate } from 'react-router-dom';
import Layout from '../components/Layout';
import ScrollReveal from '../components/ScrollReveal';
import { caseStudies, listSlugs } from '../data/caseStudies';
import { resolveImageSrc, resolveVideoSrc, resolveVideoPosterSrc } from '../utils/cloudinary';
import { ArrowLeft, ArrowRight, ExternalLink, ChevronLeft, ChevronRight, X } from 'lucide-react';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from '../components/ui/carousel';

const DEFAULT_IMAGE_SIZE = { width: 1920, height: 1080 };
const DEFAULT_VIDEO_SIZE = { width: 1920, height: 1080 };
const DEFAULT_CAROUSEL_IMAGE_SIZE = { width: 1400, height: 788 };
const DEFAULT_ROW_IMAGE_SIZE = { width: 900, height: 506 };

const LightboxContext = createContext(null);

function useLightbox() {
  return useContext(LightboxContext) || { open: () => {}, close: () => {}, isOpen: false };
}

/**
 * Parse media: string or `{ src, width?, height?, alt? }`.
 * width/height = display size only (CSS). Cloudinary always uses deliveryDefaults.
 */
function parseMedia(value, deliveryDefaults) {
  if (!value) return null;

  if (typeof value === 'string') {
    return {
      src: value,
      displayWidth: null,
      displayHeight: null,
      deliveryWidth: deliveryDefaults.width,
      deliveryHeight: deliveryDefaults.height,
    };
  }

  if (typeof value === 'object' && (value.src || value.id)) {
    return {
      src: value.src || value.id,
      displayWidth: value.width ?? null,
      displayHeight: value.height ?? null,
      deliveryWidth: deliveryDefaults.width,
      deliveryHeight: deliveryDefaults.height,
      alt: value.alt,
    };
  }

  return null;
}

function mediaDisplayStyle(media) {
  if (!media?.displayWidth && !media?.displayHeight) return undefined;
  return {
    maxWidth: media.displayWidth ? `${media.displayWidth}px` : undefined,
    maxHeight: media.displayHeight ? `${media.displayHeight}px` : undefined,
    width: '100%',
    marginLeft: 'auto',
    marginRight: 'auto',
    display: 'block',
    objectFit: 'contain',
  };
}

function Tag({ children, dark }) {
  return (
    <span className={`px-2 py-0.5 rounded text-[10px] uppercase tracking-wider ${dark ? 'bg-white/15 text-white' : 'bg-cream-200 text-inkwell-700'}`}>{children}</span>
  );
}

function MetaList({ meta }) {
  return (
    <dl className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-5 mt-12 border-cream-300/60 border" style={{ backgroundColor: '#ece9e0', borderRadius: '16px', padding: '2rem 1.5rem' }}>
      {Object.entries(meta).map(([k, items]) => (
        <div key={k}>
          <dt className=" text-[13px] uppercase tracking-wider text-inkwell-500 mb-2">{k}</dt>
          <dd className="text-[13px] text-inkwell-900 leading-relaxed">
            {items.map((it, i) => (
              <div key={i}>{it}</div>
            ))}
          </dd>
        </div>
      ))}
    </dl>
  );
}

// Generative bento illustration matching the project’s color
function BentoIllustration({ item, idx }) {
  return (
    <div className="relative w-full aspect-[4/3] rounded-lg overflow-hidden flex items-end p-5" style={{ background: item.bg }}>
      <div className="absolute top-3 right-3 w-2 h-2 rounded-full bg-white/60" />
      {/* Simple geometric decoration */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        {idx === 0 && (
          <div className="flex flex-col items-center gap-2">
            <div className="w-16 h-20 rounded-md bg-white shadow-md" />
            <div className="flex gap-1.5"><span className="w-7 h-2 rounded bg-white" /><span className="w-3 h-2 rounded bg-white/60" /></div>
          </div>
        )}
        {idx === 1 && (
          <div className="flex items-end gap-1.5 h-20">
            {[40, 70, 55, 90, 75].map((h, i) => (
              <div key={i} className="w-3 rounded-t bg-white shadow-sm" style={{ height: `${h}%` }} />
            ))}
          </div>
        )}
        {idx === 2 && (
          <div className="relative w-24 h-24">
            <div className="absolute inset-0 rounded-full border-4 border-white/70" />
            <div className="absolute inset-2 rounded-full bg-white shadow" />
            <div className="absolute inset-7 rounded-full bg-rose-dusty" />
          </div>
        )}
      </div>
      <div className="relative">
        <h4 className=" text-lg text-inkwell-900 leading-tight">{item.title}</h4>
        {item.subtitle && <p className="text-[11.5px] text-inkwell-700 mt-1.5 leading-snug">{item.subtitle}</p>}
      </div>
    </div>
  );
}

function MediaImageBlocks({ blocks, className = 'mt-6 space-y-6' }) {
  if (!blocks.length) return null;

  return (
    <div className={className}>
      {blocks.map((block, i) => {
        if (block.type === 'row') {
          return (
            <div key={`row-${i}`} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {block.items.map((img, j) => (
                <CaseStudyImage
                  key={`${img.src}-${j}`}
                  media={img}
                  deliveryDefaults={DEFAULT_ROW_IMAGE_SIZE}
                />
              ))}
            </div>
          );
        }
        return (
          <CaseStudyImage key={`${block.item.src}-${i}`} media={block.item} alt={block.item.alt} />
        );
      })}
    </div>
  );
}

function CaseStudyVideo({ video, videoPoster, className = '' }) {
  const media = parseMedia(video, DEFAULT_VIDEO_SIZE);
  if (!media?.src) return null;

  const videoSrc = resolveVideoSrc(media.src, {
    width: media.deliveryWidth,
    height: media.deliveryHeight,
  });
  const posterMedia = parseMedia(videoPoster, DEFAULT_VIDEO_SIZE);
  const posterSrc = posterMedia
    ? resolveImageSrc(posterMedia.src, {
        width: posterMedia.deliveryWidth,
        height: posterMedia.deliveryHeight,
      })
    : resolveVideoPosterSrc(undefined, media.src, {
        width: media.deliveryWidth,
        height: media.deliveryHeight,
      });
  const displayStyle = mediaDisplayStyle(media);

  return (
    <video
      key={videoSrc}
      className={`w-full rounded-xl border border-cream-300/60 shadow-sm ${className}`.trim()}
      autoPlay
      loop
      muted
      controls
      controlsList="nodownload"
      disablePictureInPicture
      playsInline
      preload="metadata"
      poster={posterSrc}
      style={{ backgroundColor: '#e8e4d7', ...displayStyle }}
      onContextMenu={(e) => e.preventDefault()}
    >
      <source src={videoSrc} type="video/mp4" />
    </video>
  );
}

function FeatureItem({ feature, idx }) {
  const imageBlocks = normalizeSectionMedia({
    image: feature.image,
    images: feature.images,
    imageAlt: feature.imageAlt,
    heading: feature.title,
  });

  return (
    <div className="border-t border-cream-300/60 py-5">
      <div className=" text-[10.5px] uppercase tracking-wider text-inkwell-400 mb-1">0{idx + 1}</div>
      <h4 className=" text-xl text-inkwell-900">{feature.title}</h4>
      {feature.desc && (
        <p className="text-[13px] text-inkwell-700 mt-1.5 leading-relaxed">{feature.desc}</p>
      )}
      <MediaImageBlocks blocks={imageBlocks} className="mt-4 space-y-4" />
      {feature.video && (
        <CaseStudyVideo
          video={feature.video}
          videoPoster={feature.videoPoster}
          className="mt-4"
        />
      )}
    </div>
  );
}

function ReflectionItem({ item }) {
  return (
    <div className="bg-cream-50 rounded-lg p-5 border border-cream-300/60">
      <h4 className=" text-lg text-inkwell-900">{item.title}</h4>
      <p className="text-[13px] text-inkwell-700 mt-2 leading-relaxed">{item.desc}</p>
    </div>
  );
}

function parseImageEntry(entry, deliveryDefaults = DEFAULT_IMAGE_SIZE) {
  const media = parseMedia(entry, deliveryDefaults);
  if (!media) return null;
  return { ...media, alt: media.alt || '' };
}

/** Returns layout blocks: { type: 'single', item } | { type: 'row', items } */
function normalizeSectionMedia(section) {
  const blocks = [];
  const singleImage = parseMedia(section.image, DEFAULT_IMAGE_SIZE);
  if (singleImage) {
    blocks.push({
      type: 'single',
      item: {
        ...singleImage,
        alt: singleImage.alt || section.imageAlt || section.heading || '',
      },
    });
  }
  (section.images || []).forEach((entry) => {
    const rowItems = Array.isArray(entry) ? entry : entry?.row;
    if (Array.isArray(rowItems)) {
      const items = rowItems.map((item) => parseImageEntry(item, DEFAULT_ROW_IMAGE_SIZE)).filter(Boolean);
      if (items.length) blocks.push({ type: 'row', items });
      return;
    }
    const item = parseImageEntry(entry);
    if (item) blocks.push({ type: 'single', item });
  });
  return blocks;
}

function CaseStudyImage({ media, alt, deliveryDefaults = DEFAULT_IMAGE_SIZE }) {
  const { open } = useLightbox();
  if (!media?.src) return null;

  const resolvedAlt = alt ?? media.alt ?? '';

  return (
    <img
      src={resolveImageSrc(media.src, {
        width: media.deliveryWidth,
        height: media.deliveryHeight,
      })}
      alt={resolvedAlt}
      className="w-full rounded-xl border border-cream-300/60 shadow-sm cursor-zoom-in transition-opacity hover:opacity-90"
      loading="lazy"
      style={mediaDisplayStyle(media)}
      onClick={() => open({
        src: resolveImageSrc(media.src, { width: DEFAULT_IMAGE_SIZE.width }),
        alt: resolvedAlt,
      })}
    />
  );
}

function ImageLightbox({ src, alt, onClose }) {
  useEffect(() => {
    if (!src) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [src, onClose]);

  if (!src) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 backdrop-blur-sm p-4 sm:p-8 cursor-zoom-out lightbox-backdrop"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      data-native-cursor
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Close image"
        className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors z-[110]"
      >
        <X className="w-5 h-5" />
      </button>
      <img
        src={src}
        alt={alt || ''}
        className="max-w-full max-h-full object-contain rounded-lg shadow-2xl lightbox-image"
        onClick={(e) => e.stopPropagation()}
      />
    </div>,
    document.body
  );
}

function LightboxProvider({ children }) {
  const [lightbox, setLightbox] = useState(null);
  const open = useCallback((payload) => setLightbox(payload), []);
  const close = useCallback(() => setLightbox(null), []);
  const value = useMemo(() => ({ open, close, isOpen: !!lightbox }), [open, close, lightbox]);

  return (
    <LightboxContext.Provider value={value}>
      {children}
      <ImageLightbox src={lightbox?.src} alt={lightbox?.alt} onClose={close} />
    </LightboxContext.Provider>
  );
}

function SectionCarousel({ slides, intervalMs = 3000 }) {
  const { open, isOpen } = useLightbox();
  const [api, setApi] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const autoTimerRef = useRef(null);

  const clearAutoTimer = useCallback(() => {
    if (autoTimerRef.current) {
      clearInterval(autoTimerRef.current);
      autoTimerRef.current = null;
    }
  }, []);

  const startAutoTimer = useCallback(() => {
    if (!api || slides.length <= 1) return;
    clearAutoTimer();
    autoTimerRef.current = setInterval(() => {
      if (api.canScrollNext()) api.scrollNext();
      else api.scrollTo(0);
    }, intervalMs);
  }, [api, slides.length, intervalMs, clearAutoTimer]);

  const scrollPrev = useCallback(() => {
    api?.scrollPrev();
    startAutoTimer();
  }, [api, startAutoTimer]);

  const scrollNext = useCallback(() => {
    api?.scrollNext();
    startAutoTimer();
  }, [api, startAutoTimer]);

  const goToSlide = useCallback((index) => {
    api?.scrollTo(index);
    startAutoTimer();
  }, [api, startAutoTimer]);

  useEffect(() => {
    if (!api) return undefined;
    const onSelect = () => setActiveIndex(api.selectedScrollSnap());
    onSelect();
    api.on('select', onSelect);
    return () => api.off('select', onSelect);
  }, [api]);

  useEffect(() => {
    // Pause auto-advance while a slide image is expanded in the lightbox.
    if (isOpen) {
      clearAutoTimer();
      return undefined;
    }
    startAutoTimer();
    return () => clearAutoTimer();
  }, [isOpen, startAutoTimer, clearAutoTimer]);

  if (!slides?.length) return null;

  return (
    <div className="mt-8">
      <Carousel setApi={setApi} opts={{ loop: true }} className="relative">
        <div className="flex items-stretch gap-2 sm:gap-4">
          <button
            type="button"
            onClick={scrollPrev}
            className="p-2 rounded-md bg-cream-200 border border-cream-300 hover:bg-cream-300 transition-colors shrink-0 self-center"
            aria-label="Previous slide"
          >
            <ChevronLeft className="w-4 h-4 text-inkwell-700" />
          </button>
          <div className="flex-1 min-w-0">
            <CarouselContent className="-ml-0">
              {slides.map((slide, i) => (
                <CarouselItem key={`${slide.title}-${i}`} className="pl-0 basis-full">
                  <div className="rounded-xl border border-cream-300/60 bg-cream-50 overflow-hidden shadow-sm">
                    {slide.image && (() => {
                      const imageMedia = parseMedia(slide.image, DEFAULT_CAROUSEL_IMAGE_SIZE);
                      if (!imageMedia) return null;
                      const imageStyle = mediaDisplayStyle(imageMedia);
                      return (
                        <img
                          src={resolveImageSrc(imageMedia.src, {
                            width: imageMedia.deliveryWidth,
                            height: imageMedia.deliveryHeight,
                          })}
                          alt={slide.imageAlt || slide.title}
                          className='w-full cursor-zoom-in transition-opacity hover:opacity-90'
                          style={imageStyle}
                          loading="lazy"
                          onClick={() => open({
                            src: resolveImageSrc(imageMedia.src, { width: DEFAULT_IMAGE_SIZE.width }),
                            alt: slide.imageAlt || slide.title,
                          })}
                        />
                      );
                    })()}
                    {(slide.title || slide.desc || slide.description) && (
                      <div className="p-4">
                        {slide.title && (
                          <h4 className="text-base sm:text-xl md:text-xl leading-snug text-inkwell-900">{slide.title}</h4>
                        )}
                        {(slide.desc || slide.description) && (
                          <p className={`text-[13px] sm:text-[14px] text-inkwell-700 leading-relaxed ${slide.title ? 'mt-2' : ''}`}>
                            {slide.desc || slide.description}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
          </div>
          <button
            type="button"
            onClick={scrollNext}
            className="p-2 rounded-md bg-cream-200 border border-cream-300 hover:bg-cream-300 transition-colors shrink-0 self-center"
            aria-label="Next slide"
          >
            <ChevronRight className="w-4 h-4 text-inkwell-700" />
          </button>
        </div>
      </Carousel>
      {slides.length > 1 && (
        <div className="flex justify-center gap-1.5 mt-5">
          {slides.map((slide, i) => (
            <button
              key={`dot-${slide.title}-${i}`}
              type="button"
              onClick={() => goToSlide(i)}
              className={`h-1.5 rounded-full transition-all ${
                i === activeIndex ? 'w-5 bg-inkwell-700' : 'w-1.5 bg-cream-300 hover:bg-cream-400'
              }`}
              aria-label={`Go to slide ${i + 1}`}
              aria-current={i === activeIndex ? 'true' : undefined}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function SectionMedia({ section }) {
  const { video, videoPoster } = section;
  const blocks = normalizeSectionMedia(section);
  if (!blocks.length && !video) return null;

  return (
    <div className="mt-6 space-y-6">
      <MediaImageBlocks blocks={blocks} className="space-y-6" />
      <CaseStudyVideo video={video} videoPoster={videoPoster} />
    </div>
  );
}

function shortCaseTitle(slug) {
  const title = caseStudies[slug]?.title;
  if (!title) return '';
  return title.split(' \u2014')[0].split(':')[0];
}

export default function CaseStudyPage() {
  const { slug } = useParams();
  const study = caseStudies[slug];

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  if (!study) return <Navigate to="/home" replace />;

  const allSlugs = listSlugs();
  const idx = allSlugs.indexOf(slug);
  const prevSlug = idx > 0 ? allSlugs[idx - 1] : null;
  const nextSlug = idx < allSlugs.length - 1 ? allSlugs[idx + 1] : null;

  const heroDark = study.heroDark;

  return (
    <Layout>
      <LightboxProvider>
      <div className="max-w-6xl mx-auto" key={slug}>
        {/* Back link */}
        <div className="mb-6">
          <Link to="/home" className="inline-flex items-center gap-2 text-[13px] uppercase tracking-wider text-inkwell-500 hover:text-inkwell-900">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to work
          </Link>
        </div>

        {/* Hero */}
        <ScrollReveal>
          <div
            data-platform="true"
            className={`relative rounded-2xl overflow-hidden p-6 sm:p-10 md:p-14 ${heroDark ? 'text-white' : 'text-inkwell-900'}`}
            style={{ background: study.heroBg }}
          >
            <div className="flex items-center gap-2 mb-5 flex-wrap">
              {study.tags.map(t => <Tag key={t} dark={heroDark}>{t}</Tag>)}
            </div>
            <p className={`text-[11px] uppercase tracking-wider ${heroDark ? 'text-white/55' : 'text-inkwell-500'}`}>No. {study.no}</p>
            <h1 className=" text-3xl sm:text-4xl md:text-5xl leading-tight mt-3 max-w-3xl">
              <span className={`inline-block mr-2 ${heroDark ? 'text-amber-200' : 'text-amber-500'}`}>✦</span>
              {study.title}
            </h1>
            <p className={`mt-4 sm:mt-5 text-[13px] sm:text-[15px] leading-relaxed max-w-2xl ${heroDark ? 'text-white/85' : 'text-inkwell-700'}`}>{study.intro}</p>
            {study.cta && (
              <a href={study.cta.href} target="_blank" rel="noreferrer" className={`mt-7 inline-flex items-center gap-2 px-4 py-2 rounded-lg text-[11px] uppercase tracking-wider ${heroDark ? 'bg-white text-inkwell-900' : 'bg-inkwell-900 text-white'} hover:opacity-90 transition-opacity`}>
                {study.cta.label} <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
        </ScrollReveal>

        {/* Meta */}
        <ScrollReveal delay={100}>
          <MetaList meta={study.meta} />
        </ScrollReveal>

        {/* Sections */}
        {study.sections.map((s, sidx) => (
          <ScrollReveal key={`${slug}-${sidx}-${s.kind}-${s.heading || ''}`} delay={50}>
            <section className="mt-16">
              {s.label && <p className=" text-[13px] uppercase tracking-[0.25em] text-inkwell-500 mb-3" style={{ fontWeight: '500' }}>{s.label}</p>}
              {s.heading && (
                <h2 className={` ${s.kind === 'callout' ? 'text-2xl sm:text-3xl md:text-4xl' : 'text-2xl sm:text-3xl'} text-inkwell-900 leading-tight ${s.kind === 'callout' ? 'italic border-l-2 border-rose-dusty pl-6' : ''}`}>
                  {s.heading}
                </h2>
              )}
              {s.body && <p className="mt-4 text-[14px] text-inkwell-700 leading-relaxed whitespace-pre-line">{s.body}</p>}

              {s.features && (
                <div className="mt-8">
                  {s.features.map((f, i) => (
                    <ScrollReveal key={`${slug}-feature-${i}-${f.title}`} delay={i * 60}>
                      <FeatureItem feature={f} idx={i} />
                    </ScrollReveal>
                  ))}
                </div>
              )}

              {s.carousel && (
                <SectionCarousel
                  key={`${slug}-carousel-${sidx}`}
                  slides={s.carousel}
                  intervalMs={s.carouselInterval ?? 3000}
                />
              )}

              <SectionMedia section={s} />

              {s.bento && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
                  {s.bento.map((item, i) => (
                    <ScrollReveal key={`${slug}-bento-${i}-${item.title}`} delay={i * 80}>
                      <BentoIllustration item={item} idx={i} />
                    </ScrollReveal>
                  ))}
                </div>
              )}

              {s.reflections && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
                  {s.reflections.map((r, i) => (
                    <ScrollReveal key={`${slug}-reflection-${i}-${r.title}`} delay={i * 80}>
                      <ReflectionItem item={r} />
                    </ScrollReveal>
                  ))}
                </div>
              )}
            </section>
          </ScrollReveal>
        ))}

        {/* Prev / Next nav */}
        <div className="mt-20 pt-8 border-t border-cream-300/60 grid grid-cols-2 gap-6">
          <div>
            {prevSlug && (
              <Link to={`/work/${prevSlug}`} className="group block">
                <span className=" text-[10.5px] uppercase tracking-wider text-inkwell-500 flex items-center gap-2"><ArrowLeft className="w-3 h-3" /> Previous</span>
                <span className=" text-2xl text-inkwell-900 mt-1.5 block group-hover:underline">{shortCaseTitle(prevSlug)}</span>
              </Link>
            )}
          </div>
          <div className="text-right">
            {nextSlug && (
              <Link to={`/work/${nextSlug}`} className="group block">
                <span className=" text-[10.5px] uppercase tracking-wider text-inkwell-500 flex items-center gap-2 justify-end">Next <ArrowRight className="w-3 h-3" /></span>
                <span className=" text-2xl text-inkwell-900 mt-1.5 block group-hover:underline">{shortCaseTitle(nextSlug)}</span>
              </Link>
            )}
          </div>
        </div>

        <div className="h-8" />
      </div>
      </LightboxProvider>
    </Layout>
  );
}
